import { Fragment, useEffect, useState } from 'react';
import SteeringWheelIcon from 'media/images/steering_wheel.svg';
import seatMaps from 'config/seat-map.json';
import 'media/css/seating.scss';

// --- Constants ---
export const SEAT_NOTE = [
  { label: 'Ghế không bán', value: 'disabled' },
  { label: 'Đang chọn', value: 'selected' },
  { label: 'Ghế cuối', value: 'last' },
  { label: 'Ghế giữa', value: 'middle' },
  { label: 'Ghế đầu', value: 'head' },
];

// --- Utils ---
function selectSeat(seats: string[][], rowIndex: number, colIndex: number): string[][] {
  const isLastRow = rowIndex === seats.length - 1;
  const isFirstRow = rowIndex === 0;

  return seats.map((row, i) =>
    row.map((seat, j) => {
      if (i === rowIndex && j === colIndex) {
        const defaultType = isFirstRow ? 'head' : isLastRow ? 'last' : 'middle';
        return seat === 'selected' ? defaultType : 'selected';
      }
      return seat;
    })
  );
}

function parseSelectedString(input: string): { row: number; col: number }[] {
  return input
    .trim()
    .split('|')
    .map((pair) => {
      const [row, col] = pair.split(',').map(Number);
      return { row, col };
    });
}

function applySelectedSeats(seatMap: string[][], selected: { row: number; col: number }[]): string[][] {
  return selected.reduce((map, seat) => selectSeat(map, seat.row - 1, seat.col - 1), seatMap);
}

// --- Components ---

export default function SeatContainer({
  seatMap = seatMaps[0].value,
  handleConvertSeatAvailableText,
  seatAvailable = '',
}: {
  seatMap: string;
  handleConvertSeatAvailableText: (seat: string) => void;
  seatAvailable: string;
}) {
  const [seats, setSeats] = useState<string[][]>([]);

  useEffect(() => {
    const mapConfig = seatMaps.find((item) => item.value === seatMap);
    if (!mapConfig) return;

    let updatedMap = mapConfig.map;
    if (seatAvailable) {
      const selected = parseSelectedString(seatAvailable);
      updatedMap = applySelectedSeats(updatedMap, selected);
    }

    setSeats(updatedMap);
  }, [seatMap, seatAvailable]);

  return (
    <div id="seat-container" className="seat_container">
      <table>
        <tbody>
          {seats.map((row, rowIndex) => (
            <Fragment key={rowIndex}>
              <tr className="seat_row">
                {row.map((type, colIndex) => {
                  const isDisabled = type === 'disabled' || type === 'driver' || !type;
                  return (
                    <td
                      key={`${rowIndex}-${colIndex}`}
                      onClick={() => {
                        if (isDisabled) return;
                        setSeats(selectSeat(seats, rowIndex, colIndex));
                        handleConvertSeatAvailableText(`${rowIndex + 1},${colIndex + 1}`);
                      }}
                    >
                      <SeatIcon type={type} />
                    </td>
                  );
                })}
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const SeatIcon = ({ type }: { type: string }) => {
  if (type === 'driver') {
    return (
      <div className="driver_icon_wrap">
        <img src={SteeringWheelIcon} alt="Driver Seat" style={{ cursor: 'not-allowed' }} />
      </div>
    );
  }

  const COLORS = {
    head: 'rgb(174, 112, 255)',
    middle: 'rgb(251, 164, 66)',
    last: 'rgb(107, 214, 0)',
    selected: 'rgb(39, 174, 96)',
    disabled: 'rgb(242, 242, 242)',
  };

  const strokeColor = COLORS[type] || 'transparent';
  const fillColor = type === 'selected' ? 'rgb(139, 229, 176)' : type === 'disabled' ? 'rgb(224, 224, 224)' : 'transparent';

  const isSelected = type === 'selected';
  const isDisabled = type === 'disabled';

  return (
    <div>
      <svg
        width="40"
        height="32"
        viewBox="0 0 40 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ cursor: isDisabled || !type ? 'not-allowed' : 'pointer' }}
      >
        <rect x="8.75" y="2.75" width="22.5" height="26.5" rx="2.25" fill={fillColor} stroke={strokeColor} strokeWidth="1.5" />
        <rect
          x="10.25"
          y="11.75"
          width="14.5"
          height="5.5"
          rx="2.25"
          transform="rotate(90 10.25 11.75)"
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth="1.5"
        />
        <rect
          x="35.25"
          y="11.75"
          width="14.5"
          height="5.5"
          rx="2.25"
          transform="rotate(90 35.25 11.75)"
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth="1.5"
        />
        <rect x="8.75" y="22.75" width="22.5" height="6.5" rx="2.25" fill={fillColor} stroke={strokeColor} strokeWidth="1.5" />
        <path
          d="M20 6.333A6.67 6.67 0 0 0 13.334 13 6.67 6.67 0 0 0 20 19.667 6.67 6.67 0 0 0 26.667 13 6.669 6.669 0 0 0 20 6.333zm-1.333 10L15.333 13l.94-.94 2.394 2.387 5.06-5.06.94.946-6 6z"
          fill={isSelected ? 'rgb(39, 174, 96)' : 'transparent'}
        />
        <path
          d="M24.96 9.46l-1.42-1.42L20 11.59l-3.54-3.55-1.42 1.42L18.59 13l-3.55 3.54 1.42 1.42L20 14.41l3.54 3.55 1.42-1.42L21.41 13l3.55-3.54z"
          fill={isDisabled ? 'rgb(242, 242, 242)' : 'transparent'}
        />
      </svg>
    </div>
  );
};

